import React from 'react';
import Slider from 'components/commercetools-ui/atoms/slider';
import Image from 'frontastic/lib/image';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { tablet } from 'helpers/utils/screensizes';
import { GenericSectionContent } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { SECTION_SPACING } from '../../../helpers/rendering';

// Vega ← Methven `image-only-slider`: an autoplaying image-only carousel (peeks the next slide on
// tablet+), no text/overlay. Each card's desktop_image is a slide.

/**
 * Pins the dots 8px above the bottom of the slide, whatever its height.
 *
 * `styles/components/slider.css` positions pagination with magic percentages that switch at 800px,
 * while the slide's own height switches at Tailwind's `md`, 768px. Between those two numbers --
 * 768 to 800 inclusive, which is exactly iPad Mini portrait -- the slide is already 34rem tall but
 * the dots are still taking `top: 59%`, landing them in the middle of the image. Below 768 they
 * sit 8px off the bottom and from 801 flush against it; only that band is wrong.
 *
 * Written as arbitrary variants rather than a second <style> element on purpose: a rendering's
 * <style> is a sibling before its wrapper, and the container's first-child margin reset only
 * accounts for one of them.
 *
 * The selector has to reach four classes to outrank the legacy rule's three AND carry `!important`
 * to meet its own -- at equal specificity this would come down to stylesheet order, which is not
 * something to rely on.
 */
const DOTS_AT_BOTTOM = [
  '[&_.swiper-horizontal_.swiper-pagination-bullets.swiper-pagination-horizontal]:!top-auto',
  '[&_.swiper-horizontal_.swiper-pagination-bullets.swiper-pagination-horizontal]:!bottom-8',
].join(' ');

export default function VegaRendering({ content }: { content: GenericSectionContent }) {
  const { cards = [], additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'VegaRendering');
  const [isTabletSize] = useMediaQuery(tablet);

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`hero-slider-container w-full ${DOTS_AT_BOTTOM} ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <Slider
          slidesPerView={isTabletSize ? 1.3 : 1}
          dots
          spaceBetween={16}
          arrows={isTabletSize}
          innerArrows
          allowArrowsOnTouchDevice
          allowTouchMove
          prevButtonStyles={{ padding: '15px', left: '0px' }}
          nextButtonStyles={{ padding: '15px', right: '0px' }}
          autoplay={{ delay: 2000, disableOnInteraction: false }}
        >
          {cards.map((card, index) => (
            <div key={index} className="relative h-[18rem] md:h-[34rem] 2xl:h-[44rem]">
              {card?.desktop_image?.url && (
                <Image loading="lazy" src={card.desktop_image.url} alt={card.desktop_image.description ?? ''} fill style={{ objectFit: 'cover' }} />
              )}
            </div>
          ))}
        </Slider>
      </div>
    </>
  );
}
